/**
 * SellerQuickLinks — a hub of tap-to-go cards shown at the top of the
 * Dashboard tab so every seller feature is one click away, plus a
 * highlighted "Share your store" card that opens the share modal.
 */
import React from "react";
import { Link } from "react-router-dom";
import {
  Radio, Plus, ClipboardList, Store, Calendar, Ticket, CreditCard,
  Film, Share2, BadgeCheck, Crown, Inbox,
} from "lucide-react";

function Card({ icon: Icon, label, hint, color, onClick, to, testid, highlight }) {
  const inner = (
    <>
      <div
        className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
        style={{ backgroundColor: `${color}1A` }}
      >
        <Icon size={18} strokeWidth={2.5} style={{ color }} />
      </div>
      <div className="min-w-0">
        <div className="font-bold text-sm truncate">{label}</div>
        {hint && <div className="text-[11px] text-[#A1A1AA] truncate">{hint}</div>}
      </div>
    </>
  );
  const cls = `flex items-center gap-3 p-3 rounded-2xl border transition text-left active:scale-[0.98] ${
    highlight
      ? "bg-gradient-to-br from-[#D4FF00]/15 to-[#00F0FF]/10 border-[#D4FF00]/40 hover:border-[#D4FF00]"
      : "bg-[#261534] border-[#3D2845] hover:border-[#D4FF00]"
  }`;
  if (to) return <Link to={to} data-testid={testid} className={cls}>{inner}</Link>;
  return <button type="button" onClick={onClick} data-testid={testid} className={cls}>{inner}</button>;
}

export default function SellerQuickLinks({ onTab, onShare, hasStore }) {
  return (
    <div className="mb-6" data-testid="seller-quick-links">
      {/* Share your store — highlighted awareness banner */}
      <div
        className="card relative overflow-hidden mb-4 p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-3"
        data-testid="seller-share-cta"
      >
        <div className="absolute inset-0 bg-gradient-to-r from-[#D4FF00]/[0.08] to-transparent pointer-events-none" />
        <div className="relative flex items-center gap-3 flex-1 min-w-0">
          <div className="w-11 h-11 rounded-xl bg-[#D4FF00]/15 flex items-center justify-center shrink-0">
            <Share2 size={20} className="text-[#D4FF00]" />
          </div>
          <div className="min-w-0">
            <div className="font-display font-bold uppercase text-sm tracking-wide">Share your store with customers</div>
            <div className="text-xs text-[#A1A1AA] mt-0.5">
              Send one link — it opens your live sale instantly, or your store. Great for WhatsApp, Instagram & QR on packaging.
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={onShare}
          disabled={!hasStore}
          data-testid="seller-share-cta-btn"
          className="relative shrink-0 btn-primary flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
          title={hasStore ? "Share your store" : "Create a store first"}
        >
          <Share2 size={16} /> Share store link
        </button>
      </div>

      <div className="font-display font-bold uppercase text-xs tracking-widest text-[#A1A1AA] mb-2">Quick links</div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2.5">
        <Card icon={Radio} label="Go live" hint="Broadcast a sale" color="#FF007F" to="/go-live" testid="ql-go-live" />
        <Card icon={Plus} label="Add product" hint="List something new" color="#D4FF00" onClick={() => onTab("manage")} testid="ql-products" />
        <Card icon={Film} label="Studio" hint="Record & post videos" color="#00F0FF" to="/studio" testid="ql-studio" />
        <Card icon={ClipboardList} label="Orders" hint="Fulfil & track" color="#D4FF00" onClick={() => onTab("orders")} testid="ql-orders" />
        <Card icon={Store} label="Stores" hint="Manage storefronts" color="#00F0FF" onClick={() => onTab("stores")} testid="ql-stores" />
        <Card icon={Calendar} label="Rent products" hint="Rental listings" color="#FF007F" onClick={() => onTab("rentals")} testid="ql-rentals" />
        <Card icon={Ticket} label="Coupons" hint="Run discounts" color="#D4FF00" onClick={() => onTab("coupons")} testid="ql-coupons" />
        <Card icon={CreditCard} label="Payments" hint="Payout methods" color="#00F0FF" onClick={() => onTab("payments")} testid="ql-payments" />
        <Card icon={Crown} label="Plans" hint="Upgrade your plan" color="#D4FF00" onClick={() => onTab("plans")} testid="ql-plans" />
        <Card icon={Inbox} label="Inbox" hint="Buyer messages" color="#00F0FF" onClick={() => onTab("inbox")} testid="ql-inbox" />
        <Card icon={BadgeCheck} label="Verification" hint="KYC & trust" color="#FF007F" onClick={() => onTab("kyc")} testid="ql-kyc" />
      </div>
    </div>
  );
}
